/**
 * Safe Firestore utilities to prevent "INTERNAL ASSERTION FAILED" errors
 *
 * Firebase Firestore SDK v11.x has a known bug where onSnapshot listeners
 * can throw internal assertion errors during rapid navigation/unmounting.
 *
 * ROOT CAUSE: React StrictMode double-mounting causes Firestore watch stream
 * state machine corruption when components unmount before listeners complete
 * initialization (ca9/b815 errors).
 *
 * This module provides safe wrappers that:
 * 1. Apply 50ms debounce before creating listeners (prevents StrictMode races)
 * 2. Wrap unsubscribe in try-catch to prevent crashes
 * 3. Use isMounted flag to prevent state updates after unmount
 *
 * @see https://github.com/firebase/firebase-js-sdk/issues/8052
 * @see DEBUG_COMPREHENSIVE_ROOT_CAUSE.md for detailed analysis
 */

/* eslint-disable no-restricted-imports */
// This is a low-level utility that MUST import directly from firebase/firestore
// All application code should use firestoreClient.ts, which uses this utility
import {
  onSnapshot,
  type Query,
  type DocumentReference,
  type QuerySnapshot,
  type DocumentSnapshot,
  type FirestoreError,
  type Unsubscribe,
} from 'firebase/firestore';
/* eslint-enable no-restricted-imports */

/**
 * Creates a safe unsubscribe function that won't throw
 */
function createSafeUnsubscribe(unsubscribe: Unsubscribe): Unsubscribe {
  return () => {
    try {
      unsubscribe();
    } catch (error) {
      // Silently ignore Firestore internal assertion errors during cleanup
      // These are safe to ignore as the component is unmounting anyway
      if (error instanceof Error && !error.message.includes('INTERNAL ASSERTION FAILED')) {
        // Only warn on unexpected errors (not internal assertions)
        console.warn('[safeFirestore] Unexpected error during unsubscribe:', error);
      }
    }
  };
}

/**
 * Safe onSnapshot wrapper for queries
 * Prevents crashes from Firestore internal assertion errors
 */
export function safeOnSnapshot<T>(
  query: Query<T>,
  onNext: (snapshot: QuerySnapshot<T>) => void,
  onError?: (error: FirestoreError) => void
): Unsubscribe {
  try {
    const unsubscribe = onSnapshot(
      query,
      onNext,
      (error) => {
        // Suppress internal assertion errors and AbortErrors
        // These are expected during React StrictMode cleanup and fast navigation
        const isInternalError = error.message?.includes('INTERNAL ASSERTION FAILED');
        const isAbortError = error.name === 'AbortError' || error.message?.includes('aborted');
        if (!isInternalError && !isAbortError) {
          onError?.(error);
        }
      }
    );

    return createSafeUnsubscribe(unsubscribe);
  } catch (error) {
    // onSnapshot itself can throw synchronous INTERNAL ASSERTION FAILED errors
    // during React StrictMode double-mount or rapid navigation
    if (error instanceof Error && !error.message.includes('INTERNAL ASSERTION FAILED')) {
      console.warn('[safeFirestore] Unexpected error creating query listener:', error);
    }
    return () => { /* no-op — listener was never created */ };
  }
}

/**
 * Safe onSnapshot wrapper for document references
 * Prevents crashes from Firestore internal assertion errors
 */
export function safeOnSnapshotDoc<T>(
  docRef: DocumentReference<T>,
  onNext: (snapshot: DocumentSnapshot<T>) => void,
  onError?: (error: FirestoreError) => void
): Unsubscribe {
  try {
    const unsubscribe = onSnapshot(
      docRef,
      onNext,
      (error) => {
        // Suppress internal assertion errors and AbortErrors
        // These are expected during React StrictMode cleanup and fast navigation
        const isInternalError = error.message?.includes('INTERNAL ASSERTION FAILED');
        const isAbortError = error.name === 'AbortError' || error.message?.includes('aborted');
        if (!isInternalError && !isAbortError) {
          onError?.(error);
        }
      }
    );

    return createSafeUnsubscribe(unsubscribe);
  } catch (error) {
    // onSnapshot itself can throw synchronous INTERNAL ASSERTION FAILED errors
    // during React StrictMode double-mount or rapid navigation
    if (error instanceof Error && !error.message.includes('INTERNAL ASSERTION FAILED')) {
      console.warn('[safeFirestore] Unexpected error creating doc listener:', error);
    }
    return () => { /* no-op — listener was never created */ };
  }
}

/**
 * Hook helper: creates an isMounted ref pattern for safe state updates
 * Use this in useEffect to track if component is still mounted
 *
 * @example
 * useEffect(() => {
 *   const isMounted = { current: true };
 *
 *   const unsubscribe = safeOnSnapshot(query, (snapshot) => {
 *     if (!isMounted.current) return;
 *     setData(snapshot.docs);
 *   });
 *
 *   return () => {
 *     isMounted.current = false;
 *     unsubscribe();
 *   };
 * }, [deps]);
 */
export function createMountedRef(): { current: boolean } {
  return { current: true };
}

/**
 * DEBOUNCED Safe onSnapshot wrapper for queries
 *
 * Applies 50ms debounce before creating Firestore listeners to prevent
 * React StrictMode double-mount race conditions that cause SDK state
 * machine corruption (ca9/b815 errors).
 *
 * Use this for hooks that run on every page (useTenantTheme, useNotifications).
 *
 * @param query - Firestore query
 * @param onNext - Snapshot callback
 * @param onError - Error callback
 * @param isMountedRef - Optional isMounted ref to prevent state updates after unmount
 * @returns Cleanup function that cancels debounce and unsubscribes
 */
export function debouncedSafeOnSnapshot<T>(
  query: Query<T>,
  onNext: (snapshot: QuerySnapshot<T>) => void,
  onError?: (error: FirestoreError) => void,
  isMountedRef?: { current: boolean }
): Unsubscribe {
  let unsubscribe: Unsubscribe | null = null;

  // Debounce Firestore operations to allow React StrictMode's
  // double-mount cycle to complete before starting SDK operations
  const timerId = setTimeout(() => {
    // Check if component is still mounted before creating listener
    if (isMountedRef && !isMountedRef.current) return;

    unsubscribe = safeOnSnapshot(query, onNext, onError);
  }, 200);

  // Return cleanup function that cancels timer and unsubscribes
  return () => {
    clearTimeout(timerId);
    if (unsubscribe) {
      unsubscribe();
    }
  };
}

/**
 * DEBOUNCED Safe onSnapshot wrapper for document references
 *
 * Applies 50ms debounce before creating Firestore listeners to prevent
 * React StrictMode double-mount race conditions that cause SDK state
 * machine corruption (ca9/b815 errors).
 *
 * Use this for hooks that run on every page (useTenantTheme, useNotifications).
 *
 * @param docRef - Firestore document reference
 * @param onNext - Snapshot callback
 * @param onError - Error callback
 * @param isMountedRef - Optional isMounted ref to prevent state updates after unmount
 * @returns Cleanup function that cancels debounce and unsubscribes
 */
export function debouncedSafeOnSnapshotDoc<T>(
  docRef: DocumentReference<T>,
  onNext: (snapshot: DocumentSnapshot<T>) => void,
  onError?: (error: FirestoreError) => void,
  isMountedRef?: { current: boolean }
): Unsubscribe {
  let unsubscribe: Unsubscribe | null = null;

  // Debounce Firestore operations to allow React StrictMode's
  // double-mount cycle to complete before starting SDK operations
  const timerId = setTimeout(() => {
    // Check if component is still mounted before creating listener
    if (isMountedRef && !isMountedRef.current) return;

    unsubscribe = safeOnSnapshotDoc(docRef, onNext, onError);
  }, 200);

  // Return cleanup function that cancels timer and unsubscribes
  return () => {
    clearTimeout(timerId);
    if (unsubscribe) {
      unsubscribe();
    }
  };
}
